import {
  AlertTriangle,
  Car,
  ChevronRight,
  ClipboardList,
  FileText,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import { formatarData } from '@/nucleo/formato/datas';
import { papeisAbremCaminho } from '@/nucleo/navegacao/mapa';
import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { PageHeader } from '@/ui/PageHeader';
import { StatCardSkeleton } from '@/ui/base/table-skeleton';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import { useContratosAtivos, useOsAtivas, useVencimentosProximos } from './dados';
import type { AlertaDeVencimento, TipoDeAlerta } from './contrato';

/** Quantos alertas aparecem antes de recolher no "+N outros alertas", como no protótipo. */
const LIMITE_DE_ALERTAS_VISIVEIS = 8;

const ICONE_DO_ALERTA: Record<TipoDeAlerta, LucideIcon> = {
  Veículo: Car,
  Condutor: ShieldCheck,
  Monitor: Users,
};

/**
 * Colunas do grid pelo número de indicadores que o papel alcança. Classe
 * literal, para o Tailwind achá-la. Em 375 px, um por linha.
 */
const COLUNAS_DOS_INDICADORES: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

/** Onde os indicadores de Termo e de OS levam: a lista de Contratos e Termos já traz as OS. */
const CAMINHO_DOS_CONTRATOS = '/credenciado/contratos';

/**
 * `/credenciado` — painel inicial do transportador credenciado.
 *
 * Segue a OS nº 001 (§4.2, até quatro componentes visuais por painel): três
 * indicadores — Contratos ativos, OS ativas, Vencimentos próximos — e o cartão
 * de Alertas de vencimento, todos da API. Os oito cartões do protótipo e o que
 * saiu estão em `README.md`.
 *
 * O que o papel não alcança não é renderizado (regra 7 de interface): o
 * indicador cuja API responde 403 some, e o atalho para tela que o papel não
 * abre (`papeisAbremCaminho`) também.
 */
export function PortalCredenciadoInicio() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const papeis = usuario?.papeis ?? [];
  const atalho = (caminho: string) => (papeisAbremCaminho(caminho, papeis) ? caminho : undefined);

  const contratos = useContratosAtivos();
  const os = useOsAtivas();
  const vencimentos = useVencimentosProximos();
  const indicadores = [contratos, os, vencimentos];

  const carregando = indicadores.some((indicador) => indicador.carregando);
  const erro = indicadores.find((indicador) => indicador.erro)?.erro ?? null;
  const tentarDeNovo = () => {
    for (const indicador of indicadores) {
      if (indicador.erro) indicador.tentarDeNovo();
    }
  };

  const stats: StatItem[] = [];
  if (!contratos.recusado) {
    stats.push({
      title: 'Contratos ativos',
      value: contratos.valor ?? 0,
      icon: FileText,
      href: atalho(CAMINHO_DOS_CONTRATOS),
    });
  }
  if (!os.recusado) {
    stats.push({
      title: 'OS ativas',
      value: os.valor ?? 0,
      icon: ClipboardList,
      href: atalho(CAMINHO_DOS_CONTRATOS),
    });
  }
  if (!vencimentos.recusado) {
    stats.push({
      title: 'Vencimentos próximos',
      value: vencimentos.valor?.length ?? 0,
      icon: AlertTriangle,
    });
  }

  const alertas = vencimentos.valor ?? [];
  const alertasVisiveis = alertas.slice(0, LIMITE_DE_ALERTAS_VISIVEIS);
  const alertasRestantes = alertas.length - alertasVisiveis.length;

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        title={`Olá${usuario?.nome ? `, ${primeiroNome(usuario.nome)}` : ''}`}
        description="Visão consolidada da sua operação no Sistema TEG."
      />

      {carregando ? (
        <div
          role="status"
          aria-live="polite"
          className={cn('grid gap-4', COLUNAS_DOS_INDICADORES[3])}
        >
          {[0, 1, 2].map((indice) => (
            <StatCardSkeleton key={indice} />
          ))}
          <p className="sr-only">Carregando o painel.</p>
        </div>
      ) : (
        <EstadoDaTela
          carregando={false}
          erro={erro}
          aoTentarDeNovo={tentarDeNovo}
          descricao="o painel"
        >
          {stats.length === 0 && vencimentos.recusado ? (
            <EmptyState
              title="Nada a mostrar no painel para o seu perfil"
              description="Use o menu ao lado para chegar às telas do seu perfil."
            />
          ) : (
            <>
              {stats.length > 0 && (
                <StatsGrid stats={stats} columns={COLUNAS_DOS_INDICADORES[stats.length]} />
              )}

              {!vencimentos.recusado && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
                      <CardTitle className="text-base">Alertas de vencimento</CardTitle>
                      {alertas.length > 0 && (
                        <Badge
                          variant="outline"
                          className="ml-auto border-warning/40 text-warning-text"
                        >
                          {alertas.length} {alertas.length === 1 ? 'pendência' : 'pendências'}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {alertas.length === 0 ? (
                      <EmptyState
                        variant="success"
                        title="Nenhum vencimento pendente"
                        description="Quando algum documento, seguro ou vistoria estiver perto de vencer, ele aparece aqui."
                      />
                    ) : (
                      <>
                        <ul className="space-y-2">
                          {alertasVisiveis.map((alerta) => (
                            <AlertaItem
                              key={alerta.id}
                              alerta={alerta}
                              aoIr={atalho(alerta.rota) ? () => navigate(alerta.rota) : undefined}
                            />
                          ))}
                        </ul>
                        {alertasRestantes > 0 && (
                          <p className="pt-2 text-center text-xs text-muted-foreground">
                            + {alertasRestantes} outros alertas
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </EstadoDaTela>
      )}
    </div>
  );
}

/**
 * Um alerta da lista. Quando o papel abre a tela que resolve o vencimento, a
 * linha inteira leva a ela (regra 9); quando não abre, o alerta fica como
 * informação, sem cara de botão.
 */
function AlertaItem({ alerta, aoIr }: { alerta: AlertaDeVencimento; aoIr?: () => void }) {
  const vencido = alerta.dias_restantes < 0;
  const urgente = !vencido && alerta.dias_restantes <= 15;
  const Icone = ICONE_DO_ALERTA[alerta.tipo];
  const interativo = aoIr !== undefined;

  return (
    <li>
      <div
        role={interativo ? 'button' : undefined}
        tabIndex={interativo ? 0 : undefined}
        onClick={aoIr}
        onKeyDown={
          interativo
            ? (evento) => {
                if (evento.key === 'Enter' || evento.key === ' ') {
                  evento.preventDefault();
                  aoIr();
                }
              }
            : undefined
        }
        className={cn(
          'flex items-center gap-3 rounded-lg border p-3',
          interativo && 'cursor-pointer transition-colors hover:bg-muted/50',
          vencido && 'border-destructive/30 bg-destructive/5',
          urgente && 'border-warning/30 bg-warning/5',
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            vencido ? 'bg-destructive/10' : urgente ? 'bg-warning/10' : 'bg-muted',
          )}
        >
          <Icone
            className={cn(
              'h-4 w-4',
              vencido ? 'text-destructive' : urgente ? 'text-warning' : 'text-muted-foreground',
            )}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{alerta.descricao}</p>
          <p className="text-xs text-muted-foreground">{alerta.tipo}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {vencido ? (
            <Badge variant="destructive" className="text-[10px]">
              Vencido há {Math.abs(alerta.dias_restantes)}d
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className={cn('text-[10px]', urgente && 'border-warning/40 text-warning-text')}
            >
              {alerta.dias_restantes === 0 ? 'Vence hoje' : `Vence em ${alerta.dias_restantes}d`}
            </Badge>
          )}
          <span className="hidden text-xs text-muted-foreground sm:inline">
            {formatarData(alerta.validade)}
          </span>
          {interativo && (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>
    </li>
  );
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] || nome;
}

export default PortalCredenciadoInicio;
